import { Title } from '@solidjs/meta'
import type { Component } from 'solid-js'
import Badge from '~/components/Badge'
import Button from '~/components/Button'
import Card from '~/components/Card'
import Container from '~/components/Container'
import Icon from '~/components/Icon'

const CardPage: Component = () => {
  return (
    <>
      <Title>Card Component - Florez Jaramillo Solutions</Title>

      <Container class="py-8">
        <div class="mb-10 mt-6 text-balance">
          <h1 class="text-4xl font-bold text-foreground mb-4">Card</h1>
          <p class="text-foreground-subtle text-xl max-w-3xl leading-relaxed">
            Contenedores versátiles para agrupar y organizar información. Las tarjetas (cards) proporcionan una base visual sólida para mostrar contenido diverso, desde simples textos hasta
            estructuras complejas con multimedia.
          </p>
        </div>

        <div class="space-y-12">
          {/* Variantes y Estilos Base */}
          <Card>
            <Card.Header
              title="Tipología de Tarjetas"
              subtitle="Elige entre elevación táctica o bordes minimalistas según el contexto visual."
              icon={<Icon icon="material-symbols-light:school-outline" class="text-primary-500" size={24} />}
            />
            <Card.Body>
              <div class="grid grid-cols-1 md:grid-cols-2 gap-8 p-10 bg-surface-muted rounded-xl border border-border">
                <div class="space-y-4">
                  <Badge color="info" variant="soft">
                    elevation (Default)
                  </Badge>
                  <Card class="bg-surface shadow-xl border-0">
                    <Card.Body>
                      <h4 class="font-bold mb-2">Shadow Elevation</h4>
                      <p class="text-sm text-foreground-subtle">Añade profundidad visual y separa el contenido del fondo mediante sombras suaves.</p>
                    </Card.Body>
                  </Card>
                </div>

                <div class="space-y-4">
                  <Badge color="secondary" variant="soft">
                    outlined
                  </Badge>
                  <Card variant="outlined" class="bg-surface">
                    <Card.Body>
                      <h4 class="font-bold mb-2">Border Outlined</h4>
                      <p class="text-sm text-foreground-subtle">Mantiene una interfaz plana y limpia, ideal para layouts densos o fondos claros.</p>
                    </Card.Body>
                  </Card>
                </div>
              </div>
            </Card.Body>
          </Card>

          {/* Anatomía de la Tarjeta */}
          <Card>
            <Card.Header
              title="Anatomía Estructural"
              subtitle="Un sistema modular que incluye cabeceras, títulos, cuerpo de contenido y acciones."
              icon={<Icon icon="material-symbols-light:layers-outline" class="text-secondary-500" size={24} />}
            />
            <Card.Body>
              <div class="p-10 bg-surface-muted rounded-xl border border-border">
                <Card class="max-w-2xl mx-auto overflow-hidden bg-surface shadow-lg">
                  <Card.Header
                    title="Análisis de Rendimiento"
                    subtitle="Reporte generado el 14 de Abril, 2024"
                    icon={<Icon icon="material-symbols-light:bar-chart" class="text-primary-500" size={24} />}
                  />
                  <Card.Body>
                    <div class="space-y-4">
                      <p class="text-foreground-subtle">
                        El rendimiento del servidor ha incrementado un <span class="text-success-500 font-bold">12%</span> respecto al mes anterior. Las optimizaciones en el motor de renderizado han
                        reducido la latencia media.
                      </p>
                      <div class="p-4 bg-surface-muted rounded-lg border border-border">
                        <code class="text-xs font-mono text-foreground-muted">Memory usage: 42% | CPU: 18%</code>
                      </div>
                    </div>
                  </Card.Body>
                  <Card.Footer class="flex justify-between items-center bg-surface-muted border-t border-border px-6 py-4">
                    <span class="text-xs text-foreground-subtle">Último sync: hace 5 min</span>
                    <div class="flex gap-2">
                      <Button variant="text" size="sm">
                        Cerrar
                      </Button>
                      <Button variant="contained" size="sm" startIcon="material-symbols-light:download">
                        Descargar PDF
                      </Button>
                    </div>
                  </Card.Footer>
                </Card>
              </div>
            </Card.Body>
          </Card>

          {/* Variantes de Borde Superior */}
          <Card>
            <Card.Header
              title="Acentos de Color y Estilo"
              subtitle="Añade significado semántico o identidad visual mediante bordes y gradientes superiores."
              icon={<Icon icon="material-symbols-light:palette-outline" class="text-amber-500" size={24} />}
            />
            <Card.Body>
              <div class="grid grid-cols-1 md:grid-cols-3 gap-6 p-10 bg-surface-muted rounded-xl border border-border">
                <Card color="primary" style="border" class="bg-surface">
                  <Card.Body>
                    <h4 class="font-bold flex items-center gap-2">
                      <div class="w-2 h-2 rounded-full bg-primary-500" /> Border
                    </h4>
                    <p class="text-xs text-foreground-subtle mt-2 italic">Acento sutil de color en el borde superior.</p>
                  </Card.Body>
                </Card>

                <Card color="success" style="container" variant="outlined" class="bg-surface">
                  <Card.Body>
                    <h4 class="font-bold flex items-center gap-2">
                      <div class="w-2 h-2 rounded-full bg-success-500" /> Container
                    </h4>
                    <p class="text-xs text-foreground-subtle mt-2 italic">Gradiente de fondo y efectos dinámicos en hover.</p>
                  </Card.Body>
                </Card>

                <Card color="warning" style="both" variant="outlined" class="bg-surface">
                  <Card.Body>
                    <h4 class="font-bold flex items-center gap-2">
                      <div class="w-2 h-2 rounded-full bg-warning-500" /> Both
                    </h4>
                    <p class="text-xs text-foreground-subtle mt-2 italic">Combinación de borde sólido y gradiente de fondo.</p>
                  </Card.Body>
                </Card>
              </div>
            </Card.Body>
          </Card>

          {/* Casos de Uso Reales */}
          <Card>
            <Card.Header
              title="Casos de Uso"
              subtitle="Ejemplos de cómo implementar tarjetas en interfaces reales de negocio."
              icon={<Icon icon="material-symbols-light:widgets-outline" class="text-indigo-500" size={24} />}
            />
            <Card.Body>
              <div class="grid grid-cols-1 md:grid-cols-2 gap-8">
                {/* Tarjeta de Producto */}
                <div class="flex flex-col gap-4">
                  <h4 class="text-xs font-bold text-foreground-subtle uppercase tracking-widest pl-2">Venta de Suscripción</h4>
                  <Card class="overflow-hidden group bg-surface">
                    <div class="h-40 bg-gradient-to-br from-primary-500 to-primary-700 flex items-center justify-center relative">
                      <Icon icon="material-symbols-light:emoji-events" class="text-white/20 absolute -right-4 -bottom-4 rotate-12" size={120} />
                      <h2 class="text-white text-3xl font-black italic tracking-tighter">PREMIUM</h2>
                    </div>
                    <Card.Body class="pt-6">
                      <div class="flex justify-between items-start mb-4">
                        <div>
                          <h3 class="text-xl font-bold">Plan Enterprise</h3>
                          <p class="text-sm text-foreground-subtle">Para equipos de gran escala</p>
                        </div>
                        <div class="text-right">
                          <p class="text-2xl font-black text-primary-600">$99</p>
                          <p class="text-[10px] text-foreground-subtle uppercase">/ al mes</p>
                        </div>
                      </div>
                      <Button class="w-full" variant="contained" color="primary" pill>
                        Suscribirse Ahora
                      </Button>
                    </Card.Body>
                  </Card>
                </div>

                {/* Tarjeta de Perfil / Autor */}
                <div class="flex flex-col gap-4">
                  <h4 class="text-xs font-bold text-foreground-subtle uppercase tracking-widest pl-2">Ficha de Usuario</h4>
                  <Card variant="outlined" class="bg-surface hover:shadow-lg transition-shadow">
                    <Card.Body class="flex items-center gap-6">
                      <div class="relative">
                        <img src="https://i.pravatar.cc/150?u=42" class="w-16 h-16 rounded-full border-4 border-border" alt="Avatar" />
                        <div class="absolute bottom-0 right-0 w-4 h-4 bg-success-500 border-2 border-surface rounded-full" />
                      </div>
                      <div class="flex-1">
                        <h4 class="font-bold text-lg">Alex Garrido</h4>
                        <p class="text-xs text-foreground-subtle flex items-center gap-1">
                          <Icon icon="material-symbols-light:work-outline" size={12} /> Senior Product Designer
                        </p>
                      </div>
                      <Button icon="material-symbols-light:more-horiz" variant="text" size="sm" />
                    </Card.Body>
                  </Card>
                </div>
              </div>
            </Card.Body>
          </Card>

          {/* API Reference */}
          <Card>
            <Card.Header
              title="API Reference"
              subtitle="Configuración técnica para Card y sus sub-componentes."
              icon={<Icon icon="material-symbols-light:code" class="text-foreground-subtle" size={24} />}
            />
            <Card.Body>
              <div class="space-y-12">
                <div class="overflow-x-auto rounded-xl border border-border">
                  <table class="w-full text-sm text-left">
                    <thead class="text-xs uppercase bg-surface-muted text-foreground-subtle">
                      <tr>
                        <th class="px-6 py-4 border-b">Propiedad</th>
                        <th class="px-6 py-4 border-b">Tipo</th>
                        <th class="px-6 py-4 border-b">Default</th>
                        <th class="px-6 py-4 border-b">Descripción</th>
                      </tr>
                    </thead>
                    <tbody class="divide-y divide-border">
                      <tr>
                        <td class="px-6 py-4 font-mono font-bold">variant</td>
                        <td class="px-6 py-4">'elevation' | 'outlined'</td>
                        <td class="px-6 py-4">'elevation'</td>
                        <td class="px-6 py-4 italic">Estilo base del contenedor.</td>
                      </tr>
                      <tr>
                        <td class="px-6 py-4 font-mono font-bold">color</td>
                        <td class="px-6 py-4">MainColorsType</td>
                        <td class="px-6 py-4">'default'</td>
                        <td class="px-6 py-4 italic text-balance">Color temático para bordes o contenedores estilizados.</td>
                      </tr>
                      <tr>
                        <td class="px-6 py-4 font-mono font-bold">style</td>
                        <td class="px-6 py-4">'border' | 'container' | 'both'</td>
                        <td class="px-6 py-4">-</td>
                        <td class="px-6 py-4 italic text-balance">Modo de aplicación del color temático.</td>
                      </tr>
                      <tr>
                        <td class="px-6 py-4 font-mono font-bold text-primary-600">clickable</td>
                        <td class="px-6 py-4">boolean</td>
                        <td class="px-6 py-4">false</td>
                        <td class="px-6 py-4 italic">Aplica efectos hover de interactividad.</td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                <div class="grid grid-cols-1 md:grid-cols-2 gap-8 text-xs">
                  <div class="p-6 bg-surface-muted rounded-xl space-y-3">
                    <h5 class="font-bold text-foreground-subtle uppercase tracking-widest">Card.Header Props</h5>
                    <ul class="space-y-2 opacity-80">
                      <li>
                        <span class="font-mono font-bold">title</span>: string | JSX.Element
                      </li>
                      <li>
                        <span class="font-mono font-bold">subtitle</span>: string | JSX.Element
                      </li>
                      <li>
                        <span class="font-mono font-bold">icon</span>: string | JSX.Element
                      </li>
                      <li>
                        <span class="font-mono font-bold">extra</span>: JSX.Element
                      </li>
                    </ul>
                  </div>
                  <div class="p-6 bg-surface-muted rounded-xl space-y-3">
                    <h5 class="font-bold text-foreground-subtle uppercase tracking-widest">Card.Title Props</h5>
                    <ul class="space-y-2 opacity-80">
                      <li>
                        <span class="font-mono font-bold">bold</span>: boolean
                      </li>
                      <li>
                        <span class="font-mono font-bold">class</span>: string
                      </li>
                      <li>
                        <span class="font-mono font-bold">children</span>: JSX.Element
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </Card.Body>
          </Card>
        </div>
      </Container>
    </>
  )
}

export default CardPage
